// src/Views/HowToFindUs.js
import React, { useEffect } from "react";
import Header from "./Header.js";
import Footer from "./Footer.js";
import logo from "../assets/images/rsc_logo_v1.png";

const WHAT3WORDS_URL = "https://w3w.co/amplifier.sandpaper.polices";
const GOOGLE_MAPS_URL =
  "https://www.google.com/maps?q=51.19694857132058,-1.2199276475361478";

const REQUIREMENTS = [
  "Suitable ear and eye protection, if you have it.",
  "Valid photo ID (which may be scanned on arrival).",
  "Suitable clothing and footwear for the weather — outdoor footwear and clothing. No camouflage clothing, please: it is a club rule.",
];

const FAQ = [
  {
    q: "I have a SGC and I am coming for my initial “Meet and Greet”. Should I bring my shotgun?",
    a: "Sorry, no — this is a non-shooting meeting to assess your suitability for the club. We have a duty to the Home Office to ensure that prospective members are of sound character before we can offer membership.",
  },
  {
    q: "I am running late; what should I do?",
    a: "Don't panic. We are a friendly and flexible club. Just let us know, and we will do our best to accommodate.",
  },
];

function HowToFindUs() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow, noarchive");
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Header />
      <div className="bg-green-50 py-16" id="how-to-find-us">
        <div className="container mx-auto px-4 max-w-3xl text-left">
          <img
            src={logo}
            alt="Romsey Shooting Club Logo"
            className="h-24 mx-auto mb-6"
          />
          <h2 className="text-4xl font-bold text-center text-green-800 mb-4">
            How to Find Us
          </h2>
          <p className="text-lg text-center text-green-700 mb-8">
            We shoot near Popham Airfield. Use the buttons below to find us.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <a
              href={WHAT3WORDS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-700 text-white py-2 px-6 rounded hover:bg-green-600 transition-colors"
            >
              What3Words: amplifier.sandpaper.polices
            </a>
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-700 text-white py-2 px-6 rounded hover:bg-green-600 transition-colors"
            >
              Open in Google Maps
            </a>
          </div>

          <section className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-2xl font-bold text-green-800 mb-4">
              Getting here from the A303
            </h3>
            <ol className="list-decimal pl-6 space-y-3 text-left text-green-900">
              <li>
                Head northbound on the A303 (towards Basingstoke).
              </li>
              <li>
                Watch for the slip road to a small service station — Euro
                Garages, with a Greggs and a drive-through Starbucks.
              </li>
              <li>
                About 150 yards past the service station, there is a layby on
                the left with a gate opening into a field (you can see it on
                Street View). This is the Test Valley Clays parking.
              </li>
              <li>
                Go through the gate, turn right, and drive down the field/hill
                to the smaller parking area at the bottom. This is Romsey
                Shooting Club's parking — please use it if possible.
              </li>
              <li>
                If it is full, the lower part of the upper field can be used.
                Please note our lower parking area can be a bit slippy and
                muddy, so drive and park with care.
              </li>
            </ol>
          </section>

          <section className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-2xl font-bold text-green-800 mb-4">
              On foot
            </h3>
            <p className="text-green-900 mb-3">
              Park, grab your kit, and walk down the hill. At the bottom there
              is a track on the left which leads into the woods to our
              location — about 200 yards. Walk in and you'll find us.
            </p>
            <p className="text-green-900 italic">
              Note: don't follow any clay shooters — they will be going to a
              different area.
            </p>
          </section>

          <section className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-2xl font-bold text-green-800 mb-4">
              What to bring
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-left text-green-900">
              {REQUIREMENTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-2xl font-bold text-green-800 mb-4">FAQ</h3>
            <div className="space-y-4">
              {FAQ.map(({ q, a }) => (
                <div key={q}>
                  <p className="font-semibold text-green-900">{q}</p>
                  <p className="text-green-900">{a}</p>
                </div>
              ))}
            </div>
          </section>

          <div className="bg-yellow-200 border-4 border-yellow-500 rounded-lg p-6 mb-8">
            <p className="text-lg font-bold text-center text-yellow-900">
              If you get lost, call Keith on 07787 430119 or Albert on
              07976 254948. See you then!
            </p>
          </div>

          <div className="text-center">
            <a
              href="/"
              className="text-green-700 hover:text-green-800 underline"
            >
              ← Back to Home
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default HowToFindUs;
